/**
 * 一次性機密交付視窗。
 *
 * 用於後端只回傳一次明碼的場景：夥伴 API Key（建立 / 重產）與 Webhook secret
 * （設定 / 輪替）。後端只存雜湊，關掉這個視窗就再也拿不回來。
 *
 * 因此本元件**刻意不提供任何隨手關閉的途徑** —— 沒有背景點擊、沒有 Esc、沒有
 * 右上角叉叉。唯一出口是勾選確認後按完成。誤觸的代價是得重新產一把，而重產
 * 會讓舊的立即失效、夥伴已上線的串接中斷；多按一下便宜得多。
 */

import { useState, type ReactNode } from 'react';

import { Button } from './Button';
import { ModalShell } from './Modal';

export function SecretHandoffModal({
  title,
  subtitle,
  warning,
  secretLabel,
  secret,
  footnote,
  details,
  onAcknowledge,
}: {
  title: string;
  subtitle?: ReactNode;
  /** 說明「弄丟會怎樣」，而不只是「請保存」 */
  warning: ReactNode;
  secretLabel: string;
  secret: string;
  footnote?: ReactNode;
  /** 附帶的唯讀資訊（label / value 交錯） */
  details?: ReactNode;
  onAcknowledge: () => void;
}): JSX.Element {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setSaved(true); // 複製即視為已取得，不必再多勾一次
    } catch {
      // 例如非 https 情境下 clipboard 不可用 —— 保持未勾，讓使用者手動選取複製
      setCopied(false);
    }
  };

  return (
    <ModalShell width={620}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{title}</h2>
      {subtitle && <p style={{ margin: '0 0 16px', fontSize: 13 }}>{subtitle}</p>}

      <div
        style={{
          background: '#fff7ed',
          border: '1px solid #fdba74',
          borderLeft: '4px solid #ea580c',
          borderRadius: 6,
          padding: '10px 14px',
          fontSize: 13,
          marginBottom: 12,
        }}
      >
        {warning}
      </div>

      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{secretLabel}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', margin: '6px 0 4px' }}>
        <code
          style={{
            flex: 1,
            fontFamily: 'monospace',
            fontSize: 13,
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            padding: '10px 12px',
            wordBreak: 'break-all',
            userSelect: 'all',
          }}
        >
          {secret}
        </code>
        <Button variant="secondary" onClick={() => void copy()} style={{ whiteSpace: 'nowrap' }}>
          {copied ? '已複製 ✓' : '複製'}
        </Button>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 11, color: '#6b7280' }}>
        {footnote ?? '請以安全管道（如密碼管理器分享連結）交付，勿以明文 email 或即時通訊傳送。'}
      </p>

      {details && (
        <dl
          style={{
            fontSize: 12,
            color: '#374151',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 12px',
            margin: '0 0 18px',
          }}
        >
          {details}
        </dl>
      )}

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          marginBottom: 14,
          cursor: 'pointer',
        }}
      >
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        我已複製並安全保存
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={onAcknowledge} disabled={!saved}>
          完成
        </Button>
      </div>
    </ModalShell>
  );
}
